import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { getCustomerGroups, createCustomerGroup, updateCustomerGroup, deleteCustomerGroup } from "../lib/customerGroups";
import { getPriceLists } from "../lib/priceLists";

const ErrorBar = ({ error, onDismiss }) => error ? (
  <div role="alert" style={{ display: "flex", alignItems: "center", gap: 10, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "8px 12px", margin: "8px 0", fontSize: 13 }}>
    <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
    <button style={S.btn("secondary")} onClick={onDismiss}>Dismiss</button>
  </div>
) : null;

// ── Create / Edit modal ───────────────────────────────────────────────────────
function GroupForm({ initial, priceLists, onCancel, onSave }) {
  const seed = initial || {};
  const [f, setF] = useState({ name: seed.name || "", price_list_id: seed.price_list_id || "", notes: seed.notes || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.name.trim()) { setError("Name is required."); return; }
    setBusy(true); setError("");
    try { await onSave({ ...f, price_list_id: f.price_list_id || null }); } catch (e) { setError(e.message); setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 460 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{seed.id ? "Edit Group" : "New Customer Group"}</h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>
        <div style={{ marginBottom: 10 }}><label style={S.label}>Name *</label>
          <input style={S.input} value={f.name} onChange={(e) => set("name")(e.target.value)} placeholder="Wholesale, Hospitality…" aria-label="Group name" /></div>
        <div style={{ marginBottom: 10 }}><label style={S.label}>Default price list</label>
          <select style={S.input} value={f.price_list_id} onChange={(e) => set("price_list_id")(e.target.value)} aria-label="Group price list">
            <option value="">None</option>
            {priceLists.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 4 }}>Members without their own price list fall back to this one (applied by the resolver, WP11c).</div>
        </div>
        <div><label style={S.label}>Notes</label>
          <textarea style={{ ...S.input, height: 64 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} /></div>
        <ErrorBar error={error} onDismiss={() => setError("")} />
        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={S.btn()} onClick={submit} disabled={busy}>{busy ? "Saving…" : seed.id ? "Save" : "Create"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Manager (page) ─────────────────────────────────────────────────────────────
export default function CustomerGroupManager() {
  const [groups, setGroups] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);   // group | {} | null

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setGroups(await getCustomerGroups()); } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { getPriceLists().then((ls) => setPriceLists(ls.filter((l) => l.status === "active"))).catch(() => {}); }, []);

  const plName = (id) => priceLists.find((p) => p.id === id)?.name;

  const save = async (draft) => {
    if (editing && editing.id) await updateCustomerGroup(editing.id, draft);
    else await createCustomerGroup(draft);
    setEditing(null); await load();
  };
  const del = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Its members will simply be un-grouped (their customer records are untouched).`)) return;
    setBusy(true); setError("");
    try { await deleteCustomerGroup(g.id); await load(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ color: C.textMuted, fontSize: 13 }}>Group customers to share a default price list.</div>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{groups.length} group{groups.length === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={() => setEditing({})}>+ New Group</button>
      </div>

      <ErrorBar error={error} onDismiss={() => setError("")} />

      <div style={S.card}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Name</th><th style={S.th}>Default price list</th><th style={S.th}>Members</th><th style={S.th}>Notes</th><th style={S.th} aria-label="Actions"></th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={S.empty}>Loading…</td></tr>
            ) : groups.length === 0 ? (
              <tr><td colSpan={5} style={S.empty}>
                <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No customer groups yet</div>
                <div style={{ marginBottom: 12 }}>Create a group and point it at a default price list.</div>
                <button style={S.btn()} onClick={() => setEditing({})}>+ New Group</button>
              </td></tr>
            ) : groups.map((g) => (
              <tr key={g.id} style={{ cursor: "pointer" }} onClick={() => setEditing(g)}>
                <td style={S.td}><strong>{g.name}</strong></td>
                <td style={S.td}>{g.price_list_id ? (plName(g.price_list_id) || <span style={{ color: C.textDim }}>(a price list)</span>) : <span style={{ color: C.textDim }}>None</span>}</td>
                <td style={S.td}>{g.member_count}</td>
                <td style={S.td}>{g.notes ? <span style={{ color: C.textMuted, fontSize: 12 }}>{g.notes}</span> : <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={S.td} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${g.name}`} onClick={() => setEditing(g)}>✎</button>
                    <button style={S.btn("ghost")} title="Delete" aria-label={`Delete ${g.name}`} onClick={() => del(g)} disabled={busy}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && <GroupForm initial={editing} priceLists={priceLists} onCancel={() => setEditing(null)} onSave={save} />}
    </div>
  );
}
